import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  Output,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CdkOverlayOrigin, ConnectedPosition} from '@angular/cdk/overlay';
import {ObjectType} from '../../../shared/models/object.model';
import {Tag, TagType} from '../../../shared/models/tag';
import {TagService} from '../../../core/services/tag.service';
import {PopoverMultiSelectComponent} from '../popover-multi-select/popover-multi-select.component';
import {PillComponent} from '../pill/pill.component';
import {Option, OptionGroupMultiSelect} from '../../../shared/models/option';

@Component({
  selector: 'app-tag-input',
  templateUrl: './tag-input.component.html',
  styleUrls: ['./tag-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CdkOverlayOrigin,
    PopoverMultiSelectComponent,
    PillComponent,
  ],
})
export class TagInputComponent implements OnInit {
  @Input() objectType: ObjectType;
  @Input() compact = false;
  @Input() showChevron = true;
  @Input() height: number | null = null;
  @Input() createLabel: string | null = 'Create';
  @Input() autoOpen = false;
  @Input() set existingTags(tags: Tag[]) {
    this.applied = tags ? tags.filter(t => t.Type === TagType.TAG) : [];
    this.refreshDropdownOptions();
  }
  @Output() tagsChanged = new EventEmitter<Tag[]>();
  @Output() menuClosed = new EventEmitter<void>();

  @ViewChild('fieldOrigin') fieldOrigin: CdkOverlayOrigin;

  applied: Tag[] = [];
  availableTags: Tag[] = [];
  searchTerm = '';
  menuOpen = false;
  loading = false;
  creating = false;
  dropdownOptions = new OptionGroupMultiSelect();

  readonly overlayPositions: ConnectedPosition[] = [
    {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4},
  ];

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private tagService: TagService,
  ) {}

  ngOnInit(): void {
    if (this.autoOpen) this.openMenu();
  }

  openMenu(): void {
    if (this.menuOpen) return;
    this.menuOpen = true;
    this.loading = true;
    this.tagService.getUserTags$(this.objectType).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: tags => {
        this.availableTags = tags.filter(t => t.Type === TagType.TAG);
        this.loading = false;
        this.refreshDropdownOptions();
      },
      error: () => {
        this.availableTags = [];
        this.loading = false;
        this.refreshDropdownOptions();
      },
    });
  }

  closeMenu(): void {
    this.menuOpen = false;
    this.searchTerm = '';
    if (!this.creating) this.menuClosed.emit();
  }

  get canCreate(): boolean {
    const term = this.searchTerm.trim();
    return !this.creating && !!term && !this.availableTags.some(t => t.Label === term);
  }

  onSearchTermChange(term: string): void {
    this.searchTerm = term.toUpperCase();
  }

  onOptionClicked(option: Option): void {
    const tag = this.availableTags.find(t => t.ID === option.id);
    if (!tag) return;
    const appliedIndex = this.applied.findIndex(a => a.ID === tag.ID);
    if (appliedIndex >= 0) {
      this.applied = this.applied.filter((_, i) => i !== appliedIndex);
    } else {
      this.applied = this.sortTags([...this.applied, tag]);
    }
    this.refreshDropdownOptions();
    this.tagsChanged.emit([...this.applied]);
  }

  createTag(label: string): void {
    if (this.creating) return;
    this.creating = true;
    this.searchTerm = '';
    this.tagService.createOrFindTag$(label).subscribe({
      next: tag => {
        this.availableTags = [...this.availableTags.filter(t => t.ID !== tag.ID), tag];
        if (!this.applied.some(a => a.ID === tag.ID)) {
          this.applied = this.sortTags([...this.applied, tag]);
        }
        this.refreshDropdownOptions();
        this.tagsChanged.emit([...this.applied]);
      },
      error: () => this.finishCreating(),
      complete: () => this.finishCreating(),
    });
  }

  private finishCreating(): void {
    this.creating = false;
    if (!this.menuOpen) this.menuClosed.emit();
  }

  private sortTags(tags: Tag[]): Tag[] {
    return [...tags].sort((a, b) => a.Label.localeCompare(b.Label));
  }

  removeTag(index: number): void {
    this.applied = this.applied.filter((_, i) => i !== index);
    this.refreshDropdownOptions();
    this.tagsChanged.emit([...this.applied]);
  }

  private refreshDropdownOptions(): void {
    const group = new OptionGroupMultiSelect();
    const appliedTagIds = new Set(this.applied.map(a => a.ID));
    group.options = this.availableTags.map(t => ({
      id: t.ID,
      name: t.Label,
      selected: appliedTagIds.has(t.ID),
    }));
    this.dropdownOptions = group;
  }
}
